import { useState } from 'react'

import { Box, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import {
  BankingBankAccountFragment,
  LedgerAccountSelectorF0Document,
} from 'generated/resources/graphql'

import { AvailableModules, Features } from 'generated/featureDefinitions'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/banking/components/FinishSetupAccountModal/types'
import { WizardStepLayoutF0 } from 'modules/banking/components/FinishSetupAccountModal/wizardSteps/WizardStepLayoutF0'
import {
  LedgerAccountSelectorF0,
  LedgerAccountPartialValue,
} from 'modules/banking/components/LedgerAccountSelectorF0'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface Props {
  bankAccount: BankingBankAccountFragment
  legalEntityId: number
  ledgerAccountsFeatureAvailable: boolean
}

export const FinishSetupLedgerAccountWizardStepF0 = ({
  data,
  onDataChanged,
  bankAccount,
  legalEntityId,
  ledgerAccountsFeatureAvailable,
}: WizardStepRenderParams<WizardData> & Props) => {
  // Initialize with existing ledger account if available
  const initialValue: LedgerAccountPartialValue | null = bankAccount.accountResource?.account
    ? {
        id: bankAccount.accountResource.account.id,
        name: bankAccount.accountResource.account.name,
        number: bankAccount.accountResource.account.number,
      }
    : null

  const [selectedAccount, setSelectedAccount] = useState<LedgerAccountPartialValue | null>(
    initialValue
  )

  const [showUpsellModal, setShowUpsellModal] = useState(false)

  // Check if there are any ledger accounts of type 'bank' for this legal entity
  const { data: ledgerData, loading } = useQuery(LedgerAccountSelectorF0Document, {
    variables: {
      legalEntityIds: [legalEntityId],
      filter: { type: { in: ['bank'] } },
      perPage: 1,
    },
    skip: !ledgerAccountsFeatureAvailable,
  })

  const hasLedgerAccounts =
    !loading && (ledgerData?.finance?.accountsConnection?.totalCount ?? 0) > 0

  return (
    <WizardStepLayoutF0
      title={i18n.t('banking.accounts.finish_wizard.step_2.title')}
    >
      <Box style={{ maxWidth: '500px' }} flexDirection="column" gap="s8">
        {!ledgerAccountsFeatureAvailable && (
          <>
            <F0Alert
              variant="info"
              title={i18n.t('banking.accounts.finish_wizard.step_2.upsell_title')}
              description={i18n.t('banking.accounts.finish_wizard.step_2.upsell_description')}
              action={{
                label: i18n.t('banking.accounts.finish_wizard.step_2.upsell_action'),
                onClick: () => setShowUpsellModal(true),
              }}
            />
            {showUpsellModal && (
              <UpsellingKit.FeatureModal
                moduleRequested={AvailableModules.expenses}
                featureRequested={Features.accounts_payable_management}
                requestSource="ap_banking_finish_setup_wizard"
                requestReason={ValidReasons.MissingAddon}
                isOpen={showUpsellModal}
                onClose={() => setShowUpsellModal(false)}
              />
            )}
          </>
        )}
        {ledgerAccountsFeatureAvailable && !loading && !hasLedgerAccounts && (
          <F0Alert
            variant="info"
            title={i18n.t('banking.accounts.finish_wizard.step_2.no_accounts_title')}
            description={i18n.t('banking.accounts.finish_wizard.step_2.no_accounts_description')}
          />
        )}
        {ledgerAccountsFeatureAvailable && hasLedgerAccounts && (
          <Box width="full">
            <LedgerAccountSelectorF0
              legalEntityId={legalEntityId}
              types={['bank']}
              value={selectedAccount}
              label=""
              onChange={(account) => {
                setSelectedAccount(account)
                onDataChanged({ ...data, ledgerAccountId: account?.id })
              }}
            />
          </Box>
        )}
      </Box>
    </WizardStepLayoutF0>
  )
}
